import { useEffect, useState } from "react";
import { Search, Loader2, Radio, Film, CheckCircle2, Plus, Tv, Users, Database, RefreshCw, Crown, Zap, Gamepad2, Ticket, Copy, Download, Activity, Mail } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LivePanel } from "@/components/admin/LivePanel";
import { InboxPanel } from "@/components/admin/InboxPanel";

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-card border border-white/10 rounded-xl p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-lg grid place-items-center ${color}`}><Icon size={22} /></div>
      <div>
        <div className="text-2xl font-head font-extrabold">{value ?? "—"}</div>
        <div className="text-xs text-zinc-400 uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [source, setSource] = useState("iptv");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState({});
  const [searching, setSearching] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  const research = async (e) => {
    e?.preventDefault();
    setSearching(true); setResults([]); setSelected({});
    try {
      const { data } = await api.post("/admin/research", { source, query, limit: 24 });
      setResults(data.items);
      if (!data.items.length) toast.info("No streams found. Try a different search.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Research failed");
    } finally { setSearching(false); }
  };

  const toggle = (i) => setSelected((s) => ({ ...s, [i]: !s[i] }));
  const selectAll = () => {
    const all = {}; results.forEach((_, i) => (all[i] = true)); setSelected(all);
  };

  const publish = async () => {
    const items = results.filter((_, i) => selected[i]);
    if (!items.length) { toast.error("Select at least one stream"); return; }
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/publish", { items });
      toast.success(`Published ${data.added} new title${data.added === 1 ? "" : "s"} to the library`);
      setResults([]); setSelected({}); loadStats();
    } catch {
      toast.error("Publish failed");
    } finally { setPublishing(false); }
  };

  const reseed = async () => {
    try { await api.post("/admin/reseed"); toast.success("Re-crawl started — new content will appear shortly."); }
    catch { toast.error("Reseed failed"); }
  };

  const [tab, setTab] = useState("research");
  const [members, setMembers] = useState([]);
  const [mQuery, setMQuery] = useState("");
  const [mLoading, setMLoading] = useState(false);

  const loadMembers = async (q = "") => {
    setMLoading(true);
    try { const { data } = await api.get("/admin/members", { params: { q } }); setMembers(data.members); }
    finally { setMLoading(false); }
  };
  useEffect(() => { if (tab === "members") loadMembers(mQuery); }, [tab]);

  // ---- access codes ----
  const [codes, setCodes] = useState([]);
  const [codeTotals, setCodeTotals] = useState({ total: 0, total_redemptions: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [publicPass, setPublicPass] = useState("");
  const [cLoading, setCLoading] = useState(false);
  const [genCount, setGenCount] = useState(50);
  const [genUses, setGenUses] = useState(1);
  const [genValid, setGenValid] = useState(30);
  const [generating, setGenerating] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);

  const loadCodes = async () => {
    setCLoading(true);
    try {
      const [{ data }, a, p] = await Promise.all([
        api.get("/admin/codes"),
        api.get("/admin/codes/analytics").then((r) => r.data).catch(() => null),
        api.get("/subscription/public-pass").then((r) => r.data).catch(() => null),
      ]);
      setCodes(data.codes); setCodeTotals({ total: data.total, total_redemptions: data.total_redemptions });
      setAnalytics(a); setPublicPass(p?.code || "");
    } finally { setCLoading(false); }
  };
  useEffect(() => { if (tab === "codes") loadCodes(); }, [tab]);

  const generateCodes = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/codes/generate", {
        count: Number(genCount), tier: "premium", hours: 24,
        max_redemptions: Number(genUses), valid_days: Number(genValid),
      });
      setLastBatch(data);
      toast.success(`Generated ${data.count} codes`);
      loadCodes();
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const copyCodes = (list) => {
    navigator.clipboard.writeText(list.join("\n")).then(() => toast.success(`Copied ${list.length} codes`));
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/codes/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "netimetv_access_codes.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const grant = async (email, days = 30) => {
    try { await api.post("/admin/grant", { email, days }); toast.success(`Added ${days} days to ${email}`); loadMembers(mQuery); loadStats(); }
    catch (e) { toast.error(e.response?.data?.detail || "Grant failed"); }
  };
  const revoke = async (email) => {
    try { await api.post("/admin/revoke", { email }); toast.success(`Revoked ${email}`); loadMembers(mQuery); loadStats(); }
    catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
  };

  return (
    <div className="pt-24 px-4 sm:px-8 lg:px-12 pb-20 min-h-screen" data-testid="admin-stream-research-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold flex items-center gap-3">
            <span className="text-gold">Admin</span> Dashboard
          </h1>
          <p className="text-zinc-400 mt-1">Live viewers, members, messages and your content library.</p>
        </div>
        <button data-testid="reseed-btn" onClick={reseed}
          className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <RefreshCw size={16} /> Re-crawl catalog
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-tabs">
        <button onClick={() => setTab("research")} data-testid="tab-research" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "research" ? "bg-brand text-white" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>Research & Collect</button>
        <button onClick={() => setTab("members")} data-testid="tab-members" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "members" ? "bg-brand text-white" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>Members</button>
        <button onClick={() => setTab("codes")} data-testid="tab-codes" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${tab === "codes" ? "bg-brand text-white" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}><Ticket size={15} /> Access Codes</button>
        <button onClick={() => setTab("live")} data-testid="tab-live" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${tab === "live" ? "bg-brand text-white" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}><Activity size={15} /> Live</button>
        <button onClick={() => setTab("inbox")} data-testid="tab-inbox" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${tab === "inbox" ? "bg-brand text-white" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}><Mail size={15} /> Inbox</button>
      </div>

      {tab === "live" && <LivePanel />}
      {tab === "inbox" && <InboxPanel />}

      {tab === "research" && (<>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
        <StatCard icon={Database} label="Total Titles" value={stats?.total} color="bg-brand/20 text-brand" />
        <StatCard icon={Radio} label="Live Channels" value={stats?.live} color="bg-pink/20 text-pink" />
        <StatCard icon={Gamepad2} label="Games" value={stats?.games} color="bg-gold/20 text-gold" />
        <StatCard icon={Zap} label="HD Channels" value={stats?.hd} color="bg-blue-500/20 text-blue-400" />
        <StatCard icon={Crown} label="Subscribers" value={stats?.subscribers} color="bg-green-500/20 text-green-400" />
        <StatCard icon={Users} label="Members" value={stats?.users} color="bg-white/10 text-white" />
      </div>

      <div className="bg-card border border-white/10 rounded-2xl p-6">
        <div className="flex gap-2 mb-4">
          <button onClick={() => setSource("iptv")} data-testid="source-iptv"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-brand text-white">
            <Tv size={16} /> Live TV (iptv-org)
          </button>
        </div>

        <form onSubmit={research} className="flex gap-2">
          <div className="flex-1 flex items-center bg-white/5 border border-white/15 rounded-lg px-3">
            <Search size={18} className="text-zinc-400" />
            <input data-testid="research-input" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={source === "archive" ? "e.g. horror, western, noir…" : "e.g. news, sports, BBC…"}
              className="bg-transparent outline-none px-3 py-3 w-full" />
          </div>
          <button data-testid="research-btn" disabled={searching}
            className="bg-white text-black font-bold px-6 rounded-lg hover:bg-brand hover:text-white transition-colors flex items-center gap-2 disabled:opacity-60">
            {searching ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />} Research
          </button>
        </form>

        {results.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-zinc-400">{results.length} streams found</p>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-sm text-zinc-300 hover:text-white px-3 py-1.5 rounded-lg bg-white/5">Select all</button>
                <button data-testid="publish-btn" onClick={publish} disabled={publishing}
                  className="flex items-center gap-2 bg-brand hover:bg-brand-hover font-semibold px-4 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-60">
                  {publishing ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Publish selected
                </button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 max-h-[520px] overflow-y-auto pr-1">
              {results.map((r, i) => (
                <button key={i} onClick={() => toggle(i)} data-testid={`research-result-${i}`}
                  className={`text-left flex gap-3 p-3 rounded-xl border transition-colors ${selected[i] ? "border-brand bg-brand/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}>
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-black/40 grid place-items-center shrink-0">
                    {r.poster ? <img src={r.poster} alt="" className="w-full h-full object-cover" />
                      : (r.type === "live" ? <Radio className="text-brand" /> : <Film className="text-gold" />)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{r.title}</p>
                    <p className="text-xs text-zinc-400 truncate">{r.category_label}{r.country ? ` · ${r.country}` : ""}</p>
                    <p className="text-[10px] text-zinc-500 truncate mt-1">{r.stream_url}</p>
                  </div>
                  {selected[i] && <CheckCircle2 className="text-brand shrink-0" size={20} />}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      </>)}

      {tab === "members" && (
        <div className="bg-card border border-white/10 rounded-2xl p-6" data-testid="admin-members-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="font-head text-xl font-bold">Members ({members.length})</h2>
            <form onSubmit={(e) => { e.preventDefault(); loadMembers(mQuery); }} className="flex items-center bg-white/5 border border-white/15 rounded-lg px-3">
              <Search size={16} className="text-zinc-400" />
              <input data-testid="member-search" value={mQuery} onChange={(e) => setMQuery(e.target.value)} placeholder="Search email…" className="bg-transparent outline-none px-2 py-2 text-sm w-56" />
            </form>
          </div>
          {mLoading ? (
            <div className="py-12 grid place-items-center"><Loader2 className="animate-spin text-brand" size={28} /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-white/10">
                    <th className="py-2 pr-4">Email</th><th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Last seen</th><th className="py-2 pr-4">App</th><th className="py-2 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} data-testid={`member-row-${m.email}`} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2 pr-4">{m.email}{m.role === "admin" && <span className="ml-2 text-[10px] bg-gold text-black px-1.5 rounded font-bold">ADMIN</span>}</td>
                      <td className="py-2 pr-4 text-zinc-400">{m.name}</td>
                      <td className="py-2 pr-4" data-testid={`member-status-${m.email}`}>{m.online ? <span className="text-green-400 font-semibold">● Online</span> : <span className="text-zinc-300">Member</span>}{m.paid && <span className="ml-2 text-[10px] text-gold font-bold">PAID</span>}</td>
                      <td className="py-2 pr-4 text-zinc-400" data-testid={`member-lastseen-${m.email}`}>{m.last_seen ? new Date(m.last_seen).toLocaleString() : "—"}</td>
                      <td className="py-2 pr-4" data-testid={`member-app-${m.email}`}>{m.has_app ? <span className="text-pink font-semibold">Installed</span> : <span className="text-zinc-600">—</span>}</td>
                      <td className="py-2 pr-4">
                        <div className="flex gap-2 justify-end">
                          <button data-testid={`grant-${m.email}`} onClick={() => grant(m.email, 30)} className="px-3 py-1 rounded-md bg-green-600/80 hover:bg-green-600 text-xs font-semibold">+30d</button>
                          <button onClick={() => grant(m.email, 7)} className="px-2 py-1 rounded-md bg-white/10 hover:bg-white/20 text-xs">+7d</button>
                          <button data-testid={`revoke-${m.email}`} onClick={() => revoke(m.email)} disabled={m.role === "admin"} className="px-3 py-1 rounded-md bg-brand/80 hover:bg-brand text-xs font-semibold disabled:opacity-40">Revoke</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {members.length === 0 && <p className="text-center text-zinc-500 py-8">No members found.</p>}
            </div>
          )}
        </div>
      )}

      {tab === "codes" && (
        <div data-testid="admin-codes-panel" className="space-y-6">
          {publicPass && (
            <div data-testid="public-pass-banner" className="bg-gradient-to-r from-gold/20 to-amber-500/10 border border-gold/40 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-gold font-bold mb-1">Public 24h Welcome Pass</p>
                <p className="text-sm text-zinc-300">Shown prefilled on the site banner & redeem box for every visitor to activate.</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono font-extrabold tracking-wider text-lg bg-black/40 px-3 py-1.5 rounded-lg">{publicPass}</span>
                <button data-testid="copy-public-pass" onClick={() => copyCodes([publicPass])} className="bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg text-sm flex items-center gap-1.5"><Copy size={14} /> Copy</button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Ticket} label="Active Codes" value={analytics?.active_codes ?? codeTotals.total} color="bg-gold/20 text-gold" />
            <StatCard icon={CheckCircle2} label="Redemptions" value={analytics?.total_redemptions ?? codeTotals.total_redemptions} color="bg-green-500/20 text-green-400" />
            <StatCard icon={Crown} label="Converted to Paid" value={analytics?.converted_to_paid ?? 0} color="bg-brand/20 text-brand" />
            <StatCard icon={Zap} label="Conversion Rate" value={`${analytics?.conversion_rate ?? 0}%`} color="bg-sky-500/20 text-sky-400" />
          </div>
          <p className="text-xs text-zinc-500 -mt-2">Conversion = visitors who redeemed a 24h code and later became paying (Paystack) subscribers · {analytics?.unique_redeemers ?? 0} unique redeemers · {analytics?.public_redemptions ?? 0} public-pass activations.</p>

          <div className="bg-card border border-white/10 rounded-2xl p-6">
            <h2 className="font-head text-xl font-bold mb-1 flex items-center gap-2"><Plus size={18} /> Generate codes</h2>
            <p className="text-zinc-400 text-sm mb-4">Each code grants <span className="text-gold font-semibold">24 hours of Premium</span> (all channels, radio & games) with no card required.</p>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <label className="text-sm">How many codes
                <input data-testid="gen-count" type="number" min="1" max="5000" value={genCount} onChange={(e) => setGenCount(e.target.value)}
                  className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 outline-none focus:border-brand" />
              </label>
              <label className="text-sm">Uses per code
                <input data-testid="gen-uses" type="number" min="1" value={genUses} onChange={(e) => setGenUses(e.target.value)}
                  className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 outline-none focus:border-brand" />
                <span className="text-[11px] text-zinc-500 block">1 = single-use. Higher = one shared code for many people.</span>
              </label>
              <label className="text-sm">Valid for (days)
                <input data-testid="gen-valid" type="number" min="0" value={genValid} onChange={(e) => setGenValid(e.target.value)}
                  className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 outline-none focus:border-brand" />
                <span className="text-[11px] text-zinc-500 block">0 = never expires.</span>
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="generate-codes-btn" onClick={generateCodes} disabled={generating}
                className="flex items-center gap-2 bg-gold text-black font-bold px-5 py-2.5 rounded-lg hover:brightness-110 transition disabled:opacity-60">
                {generating ? <Loader2 className="animate-spin" size={18} /> : <Ticket size={16} />} Generate
              </button>
              <button data-testid="export-codes-btn" onClick={exportCsv}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors">
                <Download size={16} /> Export all as CSV
              </button>
            </div>

            {lastBatch && (
              <div className="mt-5 bg-black/40 border border-gold/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gold">{lastBatch.count} new codes ready to hand out</p>
                  <button onClick={() => copyCodes(lastBatch.codes)} className="flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg"><Copy size={13} /> Copy all</button>
                </div>
                <div className="max-h-40 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-1.5 font-mono text-xs text-zinc-300">
                  {lastBatch.codes.map((c) => <div key={c} className="bg-white/5 rounded px-2 py-1 tracking-wider">{c}</div>)}
                </div>
              </div>
            )}
          </div>

          <div className="bg-card border border-white/10 rounded-2xl p-6">
            <h2 className="font-head text-lg font-bold mb-3">Recent codes</h2>
            {cLoading ? (
              <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-brand" size={26} /></div>
            ) : (
              <div className="overflow-x-auto max-h-[480px]">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-zinc-500 border-b border-white/10 sticky top-0 bg-card">
                    <th className="py-2 pr-4">Code</th><th className="py-2 pr-4">Unlocks</th><th className="py-2 pr-4">Used</th><th className="py-2 pr-4">Expires</th>
                  </tr></thead>
                  <tbody>
                    {codes.slice(0, 500).map((c) => (
                      <tr key={c.code} data-testid={`code-row-${c.code}`} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-2 pr-4 font-mono tracking-wider">{c.code}</td>
                        <td className="py-2 pr-4 text-zinc-400">{c.hours || 24}h {c.tier}</td>
                        <td className="py-2 pr-4">{c.redemptions}/{c.max_redemptions}</td>
                        <td className="py-2 pr-4 text-zinc-400">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {codes.length === 0 && <p className="text-center text-zinc-500 py-8">No codes yet — generate your first batch above.</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
